import {
  OrderSummaryBodyServer1,
  OrderSummaryBodyServer2,
  OrderSummaryBodyServer3,
  PermanentProxy,
  ProxyOrderSummaryResponse,
  Server3UpdateOptionsResponse,
  ProxyServer,
  PurchaseProxyRequest,
  Server1Options,
  Server2Options,
  Server3Options,
  Server1NextOptions,
  Server2NextOptions,
  Server3NextOptions,
} from '../../types/api';
import { baseApi } from './baseApi';

// Get Available Proxy Servers
export interface GetProxyServersResponse {
  status: 'success';
  message: string;
  data: {
    servers: ProxyServer[];
  };
}

// Get Server Options
export type GetServerOptionsResponse =
  | Server1Options
  | Server2Options
  | Server3Options;

// Get Next Options
export type GetServerNextOptionsResponse =
  | Server1NextOptions
  | Server2NextOptions
  | Server3NextOptions;

// Purchase Proxy
export interface PurchaseProxyResponse {
  status: 'success';
  message: string;
  data: {
    proxies: PermanentProxy[];
  };
}

// Get Proxies List
export interface GetProxiesParams {
  status?: 'active' | 'expired' | 'cancelled';
  serverId?: 1 | 2 | 3;
  page?: number;
  limit?: number;
}

export interface GetProxiesResponse {
  status: 'success';
  message: string;
  data: {
    proxies: PermanentProxy[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

// Get Proxy Details
export interface GetProxyDetailsResponse {
  status: 'success';
  message: string;
  data: {
    proxy: PermanentProxy;
  };
}

export interface ProxyActionResponse {
  status: 'success';
  message: string;
  data: {
    action: string;
    serverId: number;
    period?: number;
    proxy: PermanentProxy;
  };
}

export interface ExtendPriceResponse {
  status: 'success';
  message: string;
  data: {
    price: number;
    currency: string;
  };
}

export type UpdateServer3ProxyBody =
  | { action: 'change_region'; regionCode: string }
  | { action: 'change_country'; countryCode: string }
  | { action: 'change_carrier'; carrier: string }
  | { action: 'change_proxy_type'; proxyType: 'SOCKS5' | 'HTTPS' }
  | { action: 'toggle_rotation' };

// Public endpoint for disabled servers
export interface GetDisabledServersPublicResponse {
  status: 'success';
  data: {
    disabledServers: string[];
  };
}

export const proxiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProxyServers: builder.query<GetProxyServersResponse, void>({
      query: () => ({
        url: '/proxies/servers',
        method: 'GET',
      }),
    }),
    getDisabledServers: builder.query<GetDisabledServersPublicResponse, void>({
      query: () => ({
        url: '/servers/disabled',
        method: 'GET',
      }),
    }),
    getServerOptions: builder.query<GetServerOptionsResponse, number>({
      query: (serverId) => ({
        url: `/proxies/servers/${serverId}/options`,
        method: 'GET',
      }),
    }),
    getServerNextOptions: builder.query<
      GetServerNextOptionsResponse,
      { serverId: number; params: Record<string, string> }
    >({
      query: ({ serverId, params }) => {
        const queryString = new URLSearchParams(params).toString();
        return {
          url: `/proxies/servers/${serverId}/options/next${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
    }),
    purchaseProxy: builder.mutation<PurchaseProxyResponse, PurchaseProxyRequest>({
      query: (data) => ({
        url: '/proxies/purchase',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Proxies', 'Balance', 'Transactions'],
    }),
    getProxies: builder.query<GetProxiesResponse, GetProxiesParams>({
      query: ({ status, serverId, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (serverId) params.append('serverId', serverId.toString());
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());

        const queryString = params.toString();
        return {
          url: `/proxies${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Proxies'],
    }),
    getProxyDetails: builder.query<GetProxyDetailsResponse, string>({
      query: (proxyId) => ({
        url: `/proxies/${proxyId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, proxyId) => [{ type: 'Proxy', id: proxyId }],
    }),
    getServer3UpdateOptions: builder.query<Server3UpdateOptionsResponse, string>({
      query: (proxyId) => ({
        url: `/proxies/${proxyId}/proxylte/options`,
        method: 'GET',
      }),
      providesTags: (_result, _error, proxyId) => [{ type: 'Proxy', id: proxyId }],
    }),
    renewProxy: builder.mutation<ProxyActionResponse, { proxyId: string; days?: number; months?: number }>({
      query: ({ proxyId, days, months }) => {
        const body: Record<string, number> = {};
        if (typeof days === 'number') body.days = days;
        if (typeof months === 'number') body.months = months;
        return {
          url: `/proxies/${proxyId}/renew`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Balance', 'Transactions'],
    }),
    getProxyExtendPrice: builder.query<ExtendPriceResponse, { proxyId: string; months: number }>({
      query: ({ proxyId, months }) => ({
        url: `/proxies/${proxyId}/extend/price`,
        method: 'POST',
        body: { months },
      }),
    }),
    updateServer3Proxy: builder.mutation<
      ProxyActionResponse,
      { proxyId: string; body: UpdateServer3ProxyBody }
    >({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/proxylte`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies'],
    }),
    /** Order summary (price) before purchase. Server 1: { serviceId, orderConfig }. Server 2: { type, ipVersion, country, period, quantity }. Server 3: { proxyCount, days, proxyType, regionCode?|countryCode?, carrier?, rotation? }. */
    getOrderSummary: builder.mutation<
      ProxyOrderSummaryResponse,
      | { serverId: 1; body: OrderSummaryBodyServer1 }
      | { serverId: 2; body: OrderSummaryBodyServer2 }
      | { serverId: 3; body: OrderSummaryBodyServer3 }
    >({
      query: ({ serverId, body }) => ({
        url: `/proxies/servers/${serverId}/summary`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetProxyServersQuery,
  useGetDisabledServersQuery,
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  usePurchaseProxyMutation,
  useGetProxiesQuery,
  useGetProxyDetailsQuery,
  useGetServer3UpdateOptionsQuery,
  useRenewProxyMutation,
  useUpdateServer3ProxyMutation,
  useGetOrderSummaryMutation,
  useGetProxyExtendPriceQuery,
} = proxiesApi;

